import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
    Box,
    Button as MuiButton,
    IconButton,
    Stack,
    TextField as MuiTextField,
    Typography,
} from '@mui/material';
import { ChangeEvent, useRef } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { StudyFormInput } from '@domain/studyForm';
import type { StudyFormImageInfo } from '@wwf/sdk';

const MAX_IMAGE_WIDTH = 240;
const MAX_IMAGE_HEIGHT = 160;
const IMAGE_QUALITY = 0.72;
const SVG_MIME_TYPE = 'image/svg+xml';

function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(new Error('Cannot read image file'));
        reader.readAsDataURL(file);
    });
}

function loadHtmlImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Cannot load selected image'));
        image.src = src;
    });
}

function computeResizeDimensions(width: number, height: number) {
    const widthRatio = MAX_IMAGE_WIDTH / width;
    const heightRatio = MAX_IMAGE_HEIGHT / height;
    const ratio = Math.min(widthRatio, heightRatio, 1);
    return {
        width: Math.max(1, Math.round(width * ratio)),
        height: Math.max(1, Math.round(height * ratio)),
    };
}

async function convertImageToInlineBase64(file: File): Promise<string> {
    const dataUrl = await readFileAsDataUrl(file);
    const isSvg =
        file.type === SVG_MIME_TYPE ||
        file.name.toLowerCase().endsWith('.svg') ||
        dataUrl.startsWith(`data:${SVG_MIME_TYPE}`);
    if (isSvg) return dataUrl;

    const image = await loadHtmlImage(dataUrl);
    const { width, height } = computeResizeDimensions(
        image.width,
        image.height
    );

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Cannot create image preview');

    context.drawImage(image, 0, 0, width, height);
    return canvas.toDataURL('image/jpeg', IMAGE_QUALITY);
}

export function ImageGalleryInputs({
    questionIndex,
    readOnly = false,
}: {
    questionIndex: number;
    readOnly?: boolean;
}) {
    const { control } = useFormContext<StudyFormInput>();
    const imagesPath =
        `formDefinition.questions.${questionIndex}.images` as `formDefinition.questions.${number}.images`;
    const { fields, append, move, remove } = useFieldArray({
        control,
        name: imagesPath,
        keyName: 'fieldId',
    });
    const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

    return (
        <Stack gap={1}>
            <Typography variant="subtitle2">Images</Typography>
            <Typography variant="caption" color="text.secondary">
                Display order: images are shown in rows of up to 5, following
                this exact order.
            </Typography>
            {fields.map((image, imageIndex) => (
                <Stack key={image.fieldId} direction="row" gap={1}>
                    <Stack flex={1} gap={1}>
                        <Controller
                            name={`${imagesPath}.${imageIndex}.key` as const}
                            control={control}
                            rules={{ required: 'Image key is required' }}
                            render={({ field, fieldState }) => (
                                <MuiTextField
                                    {...field}
                                    label="Image key"
                                    fullWidth
                                    disabled={readOnly}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                        <Controller
                            name={`${imagesPath}.${imageIndex}.name` as const}
                            control={control}
                            rules={{
                                required: 'Image display name is required',
                            }}
                            render={({ field, fieldState }) => (
                                <MuiTextField
                                    {...field}
                                    label="Image display name"
                                    fullWidth
                                    disabled={readOnly}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                    </Stack>
                    <Controller
                        name={`${imagesPath}.${imageIndex}.image` as const}
                        control={control}
                        rules={{ required: 'Image value is required' }}
                        render={({ field, fieldState }) => (
                            <Stack gap={1} flex={1}>
                                <Stack
                                    direction="row"
                                    gap={1}
                                    alignItems="center"
                                >
                                    <MuiButton
                                        startIcon={<UploadFileIcon />}
                                        disabled={readOnly}
                                        onClick={() =>
                                            fileInputRefs.current[
                                                imageIndex
                                            ]?.click()
                                        }
                                    >
                                        Select image
                                    </MuiButton>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Stored inline (SVG kept vector, others
                                        resized)
                                    </Typography>
                                </Stack>
                                <input
                                    ref={element => {
                                        fileInputRefs.current[imageIndex] =
                                            element;
                                    }}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={async (
                                        event: ChangeEvent<HTMLInputElement>
                                    ) => {
                                        const file = event.target.files?.[0];
                                        if (!file) return;
                                        const nextImage =
                                            await convertImageToInlineBase64(
                                                file
                                            );
                                        field.onChange(nextImage);
                                        event.target.value = '';
                                    }}
                                />
                                {field.value ? (
                                    <Box
                                        component="img"
                                        src={field.value}
                                        alt={`Gallery ${imageIndex + 1}`}
                                        sx={{
                                            width: 80,
                                            height: 54,
                                            objectFit: 'cover',
                                            borderRadius: 1,
                                            border: theme =>
                                                `1px solid ${theme.palette.divider}`,
                                        }}
                                    />
                                ) : null}
                                {fieldState.error ? (
                                    <Typography variant="caption" color="error">
                                        {fieldState.error.message}
                                    </Typography>
                                ) : null}
                            </Stack>
                        )}
                    />
                    <IconButton
                        aria-label="Move image up"
                        onClick={() => move(imageIndex, imageIndex - 1)}
                        disabled={readOnly || imageIndex === 0}
                    >
                        <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        aria-label="Move image down"
                        onClick={() => move(imageIndex, imageIndex + 1)}
                        disabled={readOnly || imageIndex === fields.length - 1}
                    >
                        <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        aria-label="Remove image"
                        color="error"
                        onClick={() => remove(imageIndex)}
                        disabled={readOnly}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Stack>
            ))}
            {!readOnly && (
                <Box>
                    <MuiButton
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() =>
                            append({
                                key: '',
                                name: '',
                                image: '',
                            } satisfies StudyFormImageInfo)
                        }
                    >
                        Add image
                    </MuiButton>
                </Box>
            )}
        </Stack>
    );
}
